import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Dialog, Field, Input, Switch } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import { PartPicker } from "./PartPicker";
import {
  CURRENCIES,
  PART_EDGES,
  type PartEdge,
  PART_UNIT_ROLES,
  formatNumber,
  type AttributeDTO,
  type AttributeOptionDTO,
  type MeasurementKindDTO,
  type PartAttributeValueDTO,
  type PartDTO,
  type PartEdgebandDTO,
  type PartMeasurementDTO,
  type PartSupplierDTO,
  type PartUnitDTO,
  type SupplierDTO,
  type UnitDTO,
} from "../../api/manufacturing";
import { OrderUnitClient, replenishmentKeys } from "../../api/replenishment";
import { SupplierLink } from "../../components/SupplierLink";

/**
 * The four collections that hang off a part, plus its attributes.
 *
 * Each is its own table, its own endpoint and its own inline editor: a row
 * saved here is a row written to that resource, not a field of the part. That
 * is why they are tabs and not sections of the General form — measurements
 * have units, suppliers have prices and currencies, and neither collapses into
 * a box on a form without losing what makes it a row.
 */

type Translate = (key: string, fallback: string) => string;

const nothing = (t: Translate) => t("table.empty", "Nothing to show");

/**
 * A required number typed into an inline editor: blank is NaN, not 0, so the
 * row's validation catches it instead of saving a zero nobody typed.
 */
export const requiredNumber = (value: string) => (value.trim() === "" ? Number.NaN : Number(value));
/** An optional number: blank is null. */
export const optionalNumber = (value: string) => (value.trim() === "" ? null : Number(value));

/**
 * What is wrong with a number, against the rule its column follows in the
 * database; undefined when it is fine. Null passes: a blank optional field.
 */
export function numberError(
  value: number | null,
  rule: "positive" | "not-negative" | "whole",
  t: Translate,
): string | undefined {
  if (value === null) return undefined;
  if (!Number.isFinite(value)) return t("validation.required", "Required");
  if (rule === "positive" && !(value > 0)) return t("validation.positive", "Must be greater than zero");
  if (rule === "not-negative" && value < 0) return t("validation.not-negative", "Cannot be negative");
  if (rule === "whole" && !(Number.isInteger(value) && value >= 0)) return t("validation.whole-number", "A whole number, 0 or more.");
  return undefined;
}

/** Field errors with the passing fields left out; null when the row is fine. */
export function fieldErrors(checks: Record<string, string | undefined>): Record<string, string> | null {
  const errors = Object.fromEntries(Object.entries(checks).filter((entry): entry is [string, string] => entry[1] !== undefined));
  return Object.keys(errors).length > 0 ? errors : null;
}

/** An edge in words. */
/**
 * A side by what it is: TOP and BOTTOM run along the length (the long sides),
 * RIGHT and LEFT along the width; the position is Cut Rite's (0062).
 */
export function edgeLabel(edge: string, t: Translate): string {
  switch (edge) {
    case "TOP":
      return t("part.edge.long-1", "Long side 1 (top)");
    case "RIGHT":
      return t("part.edge.short-1", "Short side 1 (right)");
    case "BOTTOM":
      return t("part.edge.long-2", "Long side 2 (bottom)");
    case "LEFT":
      return t("part.edge.short-2", "Short side 2 (left)");
    default:
      return edge;
  }
}

function unitRoleLabel(role: string, t: Translate): string {
  switch (role) {
    case "PURCHASE":
      return t("part.unit-role.purchase", "Purchase");
    case "SALE":
      return t("part.unit-role.sale", "Sale");
    case "PACKAGE":
      return t("part.unit-role.package", "Package");
    default:
      return role;
  }
}

/* --------------------------------------------------------- measurements --- */

export function MeasurementsTab({
  part,
  rows,
  kinds,
  units,
  readOnly,
  onSave,
  onDelete,
}: {
  part: PartDTO;
  rows: PartMeasurementDTO[];
  kinds: MeasurementKindDTO[];
  units: UnitDTO[];
  readOnly: boolean;
  onSave: (row: PartMeasurementDTO, isNew: boolean) => Promise<void>;
  onDelete: (row: PartMeasurementDTO) => Promise<void>;
}) {
  const { t } = useTranslation();
  const kindById = useMemo(() => new Map(kinds.map((kind) => [kind.id, kind])), [kinds]);
  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);

  const cols = useMemo(() => {
    const c = defineColumns<PartMeasurementDTO>();
    return [
      c.text("kind", (row) => kindById.get(row.measurement_kind_id)?.name ?? "", {
        headerKey: "column.measurement.kind",
        priority: 1,
      }),
      c.text("value", (row) => formatNumber(row.value), {
        headerKey: "column.measurement.value",
        priority: 2,
      }),
      c.text("unit", (row) => unitById.get(row.unit_id)?.code ?? "", {
        headerKey: "column.measurement.unit",
        priority: 3,
      }),
      // The trigger's output, shown because it is the number everything else
      // compares on — a width in inches and one in millimetres only sort
      // together because this column exists.
      c.text("value_base", (row) => formatNumber(row.value_base), {
        headerKey: "column.measurement.base",
      }),
    ];
  }, [kindById, unitById]);

  // A unit can only measure what its dimension measures; the database raises
  // on a mismatch, so the picker narrows rather than letting Save discover it.
  const unitsFor = (row: PartMeasurementDTO) => {
    const dimension = kindById.get(row.measurement_kind_id)?.dimension;
    return units
      .filter((unit) => unit.is_active && unit.base_factor !== null && (!dimension || unit.dimension === dimension))
      .map((unit) => ({ value: String(unit.id), label: unit.code }));
  };

  return (
    <DataTable
      data={rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      emptyMessage={nothing(t)}
      editor={
        readOnly
          ? undefined
          : {
              fields: {
                kind: {
                  kind: "select",
                  get: (row) => String(row.measurement_kind_id),
                  set: (row, value) => ({ ...row, measurement_kind_id: Number(value), unit_id: 0 }),
                  // One row per kind: kinds another row already measures are
                  // not offered.
                  options: (row) =>
                    kinds
                      .filter(
                        (k) =>
                          k.id === row.measurement_kind_id ||
                          (k.is_active && !rows.some((other) => other.id !== row.id && other.measurement_kind_id === k.id)),
                      )
                      .map((k) => ({ value: String(k.id), label: k.name })),
                  required: true,
                },
                value: {
                  kind: "number",
                  get: (row) => row.value,
                  set: (row, value) => ({ ...row, value: requiredNumber(value) }),
                  required: true,
                },
                unit: {
                  kind: "select",
                  get: (row) => (row.unit_id === 0 ? "" : String(row.unit_id)),
                  set: (row, value) => ({ ...row, unit_id: Number(value) }),
                  options: unitsFor,
                  required: true,
                },
              },
              newRow: (): PartMeasurementDTO => ({
                id: 0,
                part_id: part.id,
                measurement_kind_id:
                  kinds.find((k) => k.is_active && !rows.some((row) => row.measurement_kind_id === k.id))?.id ?? 0,
                value: 0,
                unit_id: 0,
              }),
              validate: (row) =>
                fieldErrors({
                  kind: row.measurement_kind_id === 0 ? t("validation.required", "Required") : undefined,
                  value: numberError(row.value, "positive", t),
                  unit: row.unit_id === 0 ? t("validation.required", "Required") : undefined,
                }),
              onSave,
              onDelete,
              addLabel: t("button.add-measurement", "Add measurement"),
            }
      }
    />
  );
}

/* ------------------------------------------------------------ edgebands --- */

export function EdgebandsTab({
  partId,
  rows,
  readOnly,
  lengthMm,
  widthMm,
  onSave,
  onDelete,
}: {
  partId: number;
  rows: PartEdgebandDTO[];
  readOnly: boolean;
  /** The part's length and width, in millimetres: how long each side is. */
  lengthMm: number | null;
  widthMm: number | null;
  onSave: (row: PartEdgebandDTO, isNew: boolean) => Promise<void>;
  onDelete: (row: PartEdgebandDTO) => Promise<void>;
}) {
  const { t } = useTranslation();
  const ordered = useMemo(() => [...rows].sort((a, b) => PART_EDGES.indexOf(a.edge) - PART_EDGES.indexOf(b.edge)), [rows]);

  // How much of the side the band covers: all of it, the length given, or half (0079).
  const banded = useCallback(
    (row: PartEdgebandDTO): number | null => {
      const side = row.edge === "TOP" || row.edge === "BOTTOM" ? lengthMm : widthMm;
      if (!row.is_partial) return side;
      if (row.banded_mm != null) return side == null ? row.banded_mm : Math.min(row.banded_mm, side);
      return side == null ? null : side / 2;
    },
    [lengthMm, widthMm],
  );
  const cols = useMemo(() => {
    const c = defineColumns<PartEdgebandDTO>();
    const sideLength = (row: PartEdgebandDTO) => (row.edge === "TOP" || row.edge === "BOTTOM" ? lengthMm : widthMm);
    return [
      c.text("edge", (row) => edgeLabel(row.edge, t), { headerKey: "column.edgeband.edge", priority: 1 }),
      c.text("material", (row) => row.material_part_number ?? `#${row.material_part_id}`, {
        headerKey: "column.edgeband.material",
        priority: 1,
      }),
      c.custom(
        "length",
        (row) => sideLength(row) ?? 0,
        (row) => {
          const length = sideLength(row);
          return <span className="tabular-nums">{length == null ? "" : `${formatNumber(length)} mm`}</span>;
        },
        { header: t("part.edgeband.length", "Side length"), priority: 2, align: "right" },
      ),
      c.custom(
        "is_partial",
        (row) => (row.is_partial ? 1 : 0),
        (row) => (row.is_partial ? <Badge variant="warning" size="sm">{t("part.edgeband.partial", "In part")}</Badge> : <span />),
        { header: t("part.edgeband.partial-column", "Banded in part"), priority: 2 },
      ),
      c.custom(
        "banded_mm",
        (row) => banded(row) ?? 0,
        (row) => {
          const length = banded(row);
          if (length == null) return <span />;
          // A partial side with no length given counts as half (0079).
          return (
            <span className={row.is_partial && row.banded_mm == null ? "tabular-nums text-ink-muted" : "tabular-nums"}
              title={row.is_partial && row.banded_mm == null ? t("part.edgeband.half", "Half the side: give the length banded if you know it.") : undefined}>
              {`${formatNumber(length)} mm`}
            </span>
          );
        },
        { header: t("part.edgeband.banded", "Banded length"), priority: 2, align: "right" },
      ),
    ];
  }, [t, lengthMm, widthMm, banded]);

  // The band is a part out of 27,298: a search, so a dialog (Add bands, Change
  // band), not a cell. A row's side and its partial mark are edited in place.
  const [banding, setBanding] = useState<PartEdgebandDTO | "new" | null>(null);
  const free = PART_EDGES.filter((edge) => !rows.some((row) => row.edge === edge));
  return (
    <>
      {banding && (
        <BandDialog
          partId={partId}
          row={banding === "new" ? null : banding}
          free={free}
          onSave={onSave}
          onClose={() => setBanding(null)}
        />
      )}
      <DataTable
        data={ordered}
        columns={cols}
        getRowId={(row) => String(row.id)}
        emptyMessage={nothing(t)}
        toolbarLead={
          readOnly ? undefined : (
            <Button
              size="sm"
              variant="secondary"
              disabled={free.length === 0}
              title={free.length === 0 ? t("part.edgeband.all-banded", "Every side has its band.") : undefined}
              onClick={() => setBanding("new")}
            >
              {t("part.edgeband.add", "Add bands")}
            </Button>
          )
        }
        editor={
          readOnly
            ? undefined
            : {
                fields: {
                  edge: {
                    kind: "select",
                    get: (row) => row.edge,
                    set: (row, value) => ({ ...row, edge: value as PartEdgebandDTO["edge"] }),
                    // A side carries one band: the ones already banded by
                    // another row are not offered.
                    options: (row) =>
                      PART_EDGES.filter((edge) => edge === row.edge || !rows.some((other) => other.edge === edge)).map((edge) => ({
                        value: edge,
                        label: edgeLabel(edge, t),
                      })),
                    required: true,
                  },
                  is_partial: {
                    kind: "checkbox",
                    get: (row) => row.is_partial,
                    set: (row, value) => ({ ...row, is_partial: value, banded_mm: value ? row.banded_mm ?? null : null }),
                  },
                  banded_mm: {
                    kind: "number",
                    get: (row) => row.banded_mm ?? null,
                    set: (row, value) => ({ ...row, banded_mm: value === "" ? null : Number(value) }),
                    placeholder: t("part.edgeband.banded.half", "half"),
                    readOnly: (row) => !row.is_partial,
                  },
                },
                validate: (row) => {
                  const side = row.edge === "TOP" || row.edge === "BOTTOM" ? lengthMm : widthMm;
                  if (row.banded_mm != null && (!(row.banded_mm > 0) || (side != null && row.banded_mm > side))) {
                    return { banded_mm: t("part.edgeband.banded.invalid", "Between 0 and the side's {{n}} mm.", { n: side ?? "" }) };
                  }
                  return null;
                },
                onSave,
                onDelete,
                rowActions: (row) => (
                  <Button size="sm" variant="ghost" onClick={() => setBanding(row)}>
                    {t("part.edgeband.change", "Change band")}
                  </Button>
                ),
                deleteConfirm: (row) => ({
                  title: t("part.edgeband.delete.side", "Remove the band on {{edge}}?", { edge: edgeLabel(row.edge, t).toLowerCase() }),
                }),
              }
        }
      />
    </>
  );
}

/**
 * A band on one or more sides: the band picked once (an edgeband, stocked by
 * length), the free sides ticked. Changing a row's band keeps its side. The
 * bill's band lines follow from the edges (0078).
 */
function BandDialog({
  partId,
  row,
  free,
  onSave,
  onClose,
}: {
  partId: number;
  row: PartEdgebandDTO | null;
  free: PartEdge[];
  onSave: (row: PartEdgebandDTO, isNew: boolean) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [band, setBand] = useState<{ id: number; number: string; name: string } | null>(
    row ? { id: row.material_part_id, number: row.material_part_number ?? `#${String(row.material_part_id)}`, name: "" } : null,
  );
  const [sides, setSides] = useState<PartEdge[]>(row ? [row.edge] : free);
  const [partial, setPartial] = useState(row?.is_partial ?? false);
  const [length, setLength] = useState(row?.banded_mm == null ? "" : String(row.banded_mm));
  const banded_mm = partial && length.trim() !== "" && Number(length) > 0 ? Number(length) : null;
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!band) return;
    setSaving(true);
    try {
      if (row) {
        await onSave({ ...row, material_part_id: band.id, material_part_number: band.number, is_partial: partial, banded_mm }, false);
      } else {
        for (const edge of sides) {
          await onSave({ id: 0, part_id: partId, edge, material_part_id: band.id, is_partial: partial, banded_mm }, true);
        }
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={row ? t("part.edgeband.change.title", "Band on {{edge}}", { edge: edgeLabel(row.edge, t).toLowerCase() }) : t("part.edgeband.add", "Add bands")}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>{t("dialog.cancel", "Cancel")}</Button>
          <Button variant="primary" loading={saving} disabled={!band || sides.length === 0} onClick={() => void save()}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t("column.edgeband.material", "Band")} htmlFor="band-part" required>
          {band ? (
            <div className="flex items-center gap-2 text-[13px]">
              <span className="font-mono">{band.number}</span>
              <span className="min-w-0 flex-1 truncate text-ink-muted">{band.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setBand(null)}>{t("button.change", "Change")}</Button>
            </div>
          ) : (
            <PartPicker
              autoFocus
              placeholder={t("part.edgeband.pick", "The edgeband, by number or name")}
              onPick={(picked) => setBand({ id: picked.id, number: picked.number, name: picked.name })}
            />
          )}
        </Field>
        {!row && (
          <fieldset className="flex flex-wrap gap-3 text-[13px]">
            <legend className="mb-1 text-[12px] font-medium">{t("part.edgeband.sides", "Sides")}</legend>
            {PART_EDGES.map((edge) => {
              const open = free.includes(edge);
              return (
                <label key={edge} className={open ? "flex items-center gap-1.5" : "flex items-center gap-1.5 opacity-50"}>
                  <input
                    type="checkbox"
                    className="size-4 accent-accent"
                    disabled={!open}
                    checked={sides.includes(edge)}
                    onChange={(event) => setSides((current) => (event.target.checked ? [...current, edge] : current.filter((side) => side !== edge)))}
                  />
                  {edgeLabel(edge, t)}
                </label>
              );
            })}
          </fieldset>
        )}
        <Switch checked={partial} onChange={setPartial} label={t("part.edgeband.partial-column", "Banded in part")} />
        {partial && (
          <Field label={t("part.edgeband.banded", "Banded length")} htmlFor="band-length"
            hint={t("part.edgeband.banded.hint", "In mm, on each side ticked. Empty: half the side.")}>
            <Input id="band-length" type="number" min="0" step="any" value={length} onChange={(event) => setLength(event.target.value)} />
          </Field>
        )}
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- units --- */

export function UnitsTab({
  part,
  rows,
  units,
  suppliers,
  stockUnit,
  readOnly,
  onSave,
  onDelete,
}: {
  part: PartDTO;
  rows: PartUnitDTO[];
  units: UnitDTO[];
  suppliers: SupplierDTO[];
  stockUnit: UnitDTO | undefined;
  readOnly: boolean;
  onSave: (row: PartUnitDTO, isNew: boolean) => Promise<void>;
  onDelete: (row: PartUnitDTO) => Promise<void>;
}) {
  const { t } = useTranslation();
  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);
  const supplierById = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);

  const cols = useMemo(() => {
    const c = defineColumns<PartUnitDTO>();
    return [
      c.text("unit", (row) => unitById.get(row.unit_id)?.code ?? "", {
        headerKey: "column.part-unit.unit",
        priority: 1,
      }),
      c.text("role", (row) => unitRoleLabel(row.role, t), { headerKey: "column.part-unit.role", priority: 2 }),
      c.text("quantity", (row) => formatNumber(row.quantity_in_stock_unit), {
        headerKey: "column.part-unit.quantity",
        priority: 3,
      }),
      c.custom(
        "supplier",
        (row) => (row.supplier_id === null ? "" : (supplierById.get(row.supplier_id)?.name ?? "")),
        (row) =>
          row.supplier_id === null ? null : (
            <SupplierLink id={row.supplier_id}>{supplierById.get(row.supplier_id)?.name ?? `#${row.supplier_id}`}</SupplierLink>
          ),
        { headerKey: "column.part-unit.supplier" },
      ),
    ];
  }, [unitById, supplierById, t]);

  return (
    <>
      {stockUnit && stockUnit.base_factor === null && rows.length === 0 && (
        <p className="border-b border-line bg-warn-soft px-4 py-2 text-[12px] text-warn lg:px-6">
          {t(
            "part.unit.missing",
            "This part is stocked in {{unit}}, which has no global conversion. Add a row saying how many it holds, or quantities here cannot be compared with anything.",
            { unit: stockUnit.code },
          )}
        </p>
      )}
      <DataTable
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        emptyMessage={nothing(t)}
        editor={
          readOnly
            ? undefined
            : {
                fields: {
                  unit: {
                    kind: "select",
                    get: (row) => (row.unit_id === 0 ? "" : String(row.unit_id)),
                    set: (row, value) => ({ ...row, unit_id: Number(value) }),
                    options: units
                      .filter((unit) => unit.is_active)
                      .map((unit) => ({ value: String(unit.id), label: unit.code })),
                    required: true,
                  },
                  role: {
                    kind: "select",
                    get: (row) => row.role,
                    set: (row, value) => ({ ...row, role: value as PartUnitDTO["role"] }),
                    options: PART_UNIT_ROLES.map((role) => ({ value: role, label: unitRoleLabel(role, t) })),
                    required: true,
                  },
                  quantity: {
                    kind: "number",
                    get: (row) => row.quantity_in_stock_unit,
                    set: (row, value) => ({ ...row, quantity_in_stock_unit: requiredNumber(value) }),
                    required: true,
                  },
                  supplier: {
                    kind: "select",
                    get: (row) => (row.supplier_id === null ? "" : String(row.supplier_id)),
                    set: (row, value) => ({ ...row, supplier_id: value === "" ? null : Number(value) }),
                    options: suppliers
                      .filter((s) => s.is_active)
                      .map((s) => ({ value: String(s.id), label: `${s.code} · ${s.name}` })),
                  },
                },
                newRow: (): PartUnitDTO => ({
                  id: 0,
                  part_id: part.id,
                  unit_id: 0,
                  role: "PURCHASE",
                  quantity_in_stock_unit: 1,
                  supplier_id: null,
                }),
                validate: (row) =>
                  fieldErrors({
                    unit: row.unit_id === 0 ? t("validation.required", "Required") : undefined,
                    quantity: numberError(row.quantity_in_stock_unit, "positive", t),
                  }),
                onSave,
                onDelete,
                addLabel: t("button.add-unit", "Add unit"),
              }
        }
      />
    </>
  );
}

/* ------------------------------------------------------------ suppliers --- */

export function SuppliersTab({
  part,
  rows,
  suppliers,
  units,
  readOnly,
  onSave,
  onDelete,
}: {
  part: PartDTO;
  rows: PartSupplierDTO[];
  suppliers: SupplierDTO[];
  units: UnitDTO[];
  readOnly: boolean;
  onSave: (row: PartSupplierDTO, isNew: boolean) => Promise<void>;
  onDelete: (row: PartSupplierDTO) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const supplierById = useMemo(() => new Map(suppliers.map((s) => [s.id, s])), [suppliers]);
  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);
  // What it is ordered in: a barrel, a roll (one part each) or its truck (shared with the supplier's other parts).
  const orderUnits = useQuery({
    queryKey: replenishmentKeys.orderUnits,
    queryFn: () => new OrderUnitClient(http).findAll(),
    staleTime: 5 * 60_000,
  });
  const orderUnitById = useMemo(() => new Map((orderUnits.data ?? []).map((unit) => [unit.id, unit])), [orderUnits.data]);
  const orderUnitOf = (row: PartSupplierDTO) => (row.order_unit_id == null ? undefined : orderUnitById.get(row.order_unit_id));
  /** The order unit figure that fits its kind: stock units per barrel, or load per stock unit in a truck. */
  const figureOf = (row: PartSupplierDTO) =>
    orderUnitOf(row)?.kind === "SHARED" ? row.load_per_stock_unit : row.stock_units_per_order_unit;

  const cols = useMemo(() => {
    const c = defineColumns<PartSupplierDTO>();
    return [
      c.number("priority", (row) => row.priority, { headerKey: "column.supplier.priority", priority: 1 }),
      // The row carries the supplier's name and code; the list is the fallback
      // for a link just added, before the refetch brings them.
      c.custom(
        "supplier",
        (row) => row.supplier_name ?? supplierById.get(row.supplier_id)?.name ?? `#${row.supplier_id}`,
        (row) => (
          <span className="inline-flex min-w-0 items-baseline gap-1.5">
            <SupplierLink id={row.supplier_id} className="truncate">
              {row.supplier_name ?? supplierById.get(row.supplier_id)?.name ?? `#${row.supplier_id}`}
            </SupplierLink>
            <span className="shrink-0 text-[11px] text-ink-faint">{row.supplier_code ?? supplierById.get(row.supplier_id)?.code ?? ""}</span>
          </span>
        ),
        { headerKey: "column.supplier.name", priority: 1 },
      ),
      c.text("supplier_part_number", (row) => row.supplier_part_number ?? "", {
        headerKey: "column.supplier.part-no",
        priority: 2,
      }),
      c.text("price", (row) => formatNumber(row.price), { headerKey: "column.supplier.price", priority: 3 }),
      c.text("currency", (row) => row.currency, { headerKey: "column.supplier.currency", filter: "select" }),
      c.text("unit", (row) => (row.unit_id === null ? "" : (unitById.get(row.unit_id)?.code ?? "")), {
        headerKey: "column.supplier.unit",
      }),
      c.text("price_date", (row) => row.price_date ?? "", { headerKey: "column.supplier.price-date" }),
      // Per link, not per part: a bundle of 20 sheets at Tafisa is not
      // Uniboard's. Replenishment rounds up to the multiple, then to the MOQ.
      c.number("lead_time_days", (row) => row.lead_time_days, {
        headerKey: "column.supplier.lead-time",
        priority: 3,
      }),
      c.number("minimum_order_quantity", (row) => row.minimum_order_quantity, {
        headerKey: "column.supplier.moq",
        priority: 3,
      }),
      c.number("order_multiple", (row) => row.order_multiple, {
        headerKey: "column.supplier.order-multiple",
        priority: 3,
      }),
      // How it is bought, apart from how it is counted: whole barrels of 208 L,
      // or its truck at 1.125 sheets of load per sheet.
      c.text("order_unit", (row) => row.order_unit_code ?? orderUnitOf(row)?.code ?? "", {
        header: t("column.supplier.order-unit", "Ordered in"),
        priority: 3,
      }),
      c.number("order_unit_figure", (row) => figureOf(row), {
        header: t("column.supplier.order-unit-figure", "Stock units each / load per unit"),
        priority: 3,
        filter: "none",
      }),
      c.number("minimum_order_units", (row) => row.minimum_order_units, {
        header: t("column.supplier.minimum-order-units", "Fewest order units"),
        priority: 3,
        filter: "none",
        defaultVisible: false,
      }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", defaultVisible: false }),
    ];
  }, [supplierById, unitById, orderUnitById, t]);

  // Priority 1 is the default supplier. Sorted here rather than server-side
  // because the detail fetch returns one part's handful of rows.
  const sorted = useMemo(() => [...rows].sort((a, b) => a.priority - b.priority), [rows]);

  return (
    <DataTable
      data={sorted}
      columns={cols}
      getRowId={(row) => String(row.id)}
      emptyMessage={nothing(t)}
      editor={
        readOnly
          ? undefined
          : {
              fields: {
                priority: {
                  kind: "number",
                  get: (row) => row.priority,
                  set: (row, value) => ({ ...row, priority: requiredNumber(value) }),
                  required: true,
                },
                supplier: {
                  kind: "select",
                  get: (row) => (row.supplier_id === 0 ? "" : String(row.supplier_id)),
                  set: (row, value) => ({ ...row, supplier_id: Number(value) }),
                  options: suppliers
                    .filter((s) => s.is_active)
                    .map((s) => ({ value: String(s.id), label: `${s.code} · ${s.name}` })),
                  required: true,
                },
                supplier_part_number: {
                  kind: "text",
                  get: (row) => row.supplier_part_number,
                  set: (row, value) => ({ ...row, supplier_part_number: value === "" ? null : value }),
                },
                price: {
                  kind: "number",
                  get: (row) => row.price,
                  set: (row, value) => ({ ...row, price: optionalNumber(value) }),
                },
                currency: {
                  kind: "select",
                  get: (row) => row.currency,
                  set: (row, value) => ({ ...row, currency: value as PartSupplierDTO["currency"] }),
                  options: CURRENCIES.map((currency) => ({ value: currency, label: currency })),
                  required: true,
                },
                unit: {
                  kind: "select",
                  get: (row) => (row.unit_id === null ? "" : String(row.unit_id)),
                  set: (row, value) => ({ ...row, unit_id: value === "" ? null : Number(value) }),
                  options: units
                    .filter((unit) => unit.is_active)
                    .map((unit) => ({ value: String(unit.id), label: unit.code })),
                },
                price_date: {
                  kind: "text",
                  get: (row) => row.price_date,
                  set: (row, value) => ({ ...row, price_date: value.trim() === "" ? null : value.trim() }),
                  placeholder: "YYYY-MM-DD",
                },
                lead_time_days: {
                  kind: "number",
                  get: (row) => row.lead_time_days,
                  set: (row, value) => ({ ...row, lead_time_days: optionalNumber(value) }),
                  placeholder: t("part-supplier.lead-time.placeholder", "Supplier's"),
                },
                minimum_order_quantity: {
                  kind: "number",
                  get: (row) => row.minimum_order_quantity,
                  set: (row, value) => ({ ...row, minimum_order_quantity: optionalNumber(value) }),
                },
                order_multiple: {
                  kind: "number",
                  get: (row) => row.order_multiple,
                  set: (row, value) => ({ ...row, order_multiple: optionalNumber(value) }),
                },
                order_unit: {
                  kind: "select",
                  get: (row) => (row.order_unit_id == null ? "" : String(row.order_unit_id)),
                  // Changing the unit clears the figures of the old one: a barrel's 208 L is not a truck's load.
                  set: (row, value) => {
                    const id = value === "" ? null : Number(value);
                    if (id === row.order_unit_id) return row;
                    const kind = id == null ? undefined : orderUnitById.get(id)?.kind;
                    return {
                      ...row,
                      order_unit_id: id,
                      order_unit_code: id == null ? null : (orderUnitById.get(id)?.code ?? null),
                      stock_units_per_order_unit: kind === "PER_PART" ? row.stock_units_per_order_unit : null,
                      load_per_stock_unit: kind === "SHARED" ? (row.load_per_stock_unit ?? 1) : null,
                      minimum_order_units: kind === "PER_PART" ? row.minimum_order_units : null,
                    };
                  },
                  options: [
                    { value: "", label: t("part-supplier.order-unit.stock", "Its stock unit") },
                    ...(orderUnits.data ?? [])
                      .filter((unit) => unit.is_active)
                      .map((unit) => ({
                        value: String(unit.id),
                        label: unit.kind === "SHARED" ? `${unit.code} · ${unit.supplier_name ?? ""}` : unit.code,
                      })),
                  ],
                },
                order_unit_figure: {
                  kind: "number",
                  get: (row) => figureOf(row),
                  set: (row, value) =>
                    orderUnitOf(row)?.kind === "SHARED"
                      ? { ...row, load_per_stock_unit: optionalNumber(value) }
                      : { ...row, stock_units_per_order_unit: row.order_unit_id == null ? null : optionalNumber(value) },
                },
                minimum_order_units: {
                  kind: "number",
                  get: (row) => row.minimum_order_units,
                  set: (row, value) => ({
                    ...row,
                    minimum_order_units: orderUnitOf(row)?.kind === "PER_PART" ? optionalNumber(value) : null,
                  }),
                },
                note: {
                  kind: "text",
                  get: (row) => row.note,
                  set: (row, value) => ({ ...row, note: value === "" ? null : value }),
                },
              },
              newRow: (): PartSupplierDTO => ({
                id: 0,
                part_id: part.id,
                supplier_id: 0,
                // After the last rank, not the last row: rows arrive in any order.
                priority: Math.min(20, Math.max(0, ...rows.map((row) => row.priority)) + 1),
                supplier_part_number: null,
                price: null,
                currency: "CAD",
                unit_id: null,
                price_date: null,
                lead_time_days: null,
                minimum_order_quantity: null,
                order_multiple: null,
                order_unit_id: null,
                stock_units_per_order_unit: null,
                load_per_stock_unit: null,
                minimum_order_units: null,
                note: null,
              }),
              validate: (row) =>
                fieldErrors({
                  priority:
                    Number.isInteger(row.priority) && row.priority >= 1 && row.priority <= 20
                      ? undefined
                      : t("part-supplier.priority.range", "A rank from 1 to 20."),
                  supplier: row.supplier_id === 0 ? t("validation.required", "Required") : undefined,
                  price: numberError(row.price, "not-negative", t),
                  price_date:
                    row.price_date === null || (/^\d{4}-\d{2}-\d{2}$/.test(row.price_date) && !Number.isNaN(Date.parse(row.price_date)))
                      ? undefined
                      : t("validation.date", "A date as YYYY-MM-DD."),
                  lead_time_days: numberError(row.lead_time_days, "whole", t),
                  minimum_order_quantity: numberError(row.minimum_order_quantity, "positive", t),
                  order_multiple: numberError(row.order_multiple, "positive", t),
                  order_unit:
                    orderUnitOf(row)?.kind === "SHARED" && orderUnitOf(row)?.supplier_id !== row.supplier_id
                      ? t("part-supplier.order-unit.other-supplier", "That truck or container comes from another supplier.")
                      : undefined,
                  order_unit_figure:
                    row.order_unit_id == null
                      ? undefined
                      : (figureOf(row) ?? 0) > 0
                        ? undefined
                        : orderUnitOf(row)?.kind === "SHARED"
                          ? t("part-supplier.order-unit.load-required", "The load one stock unit takes in it, above zero.")
                          : t("part-supplier.order-unit.size-required", "How many stock units one holds, above zero."),
                  minimum_order_units:
                    row.minimum_order_units == null || (Number.isInteger(row.minimum_order_units) && row.minimum_order_units > 0)
                      ? undefined
                      : t("part-supplier.order-unit.minimum-whole", "A whole number, 1 or more."),
                }),
              onSave,
              onDelete,
              addLabel: t("button.add-supplier", "Add supplier"),
            }
      }
    />
  );
}

/* ----------------------------------------------------------- attributes --- */

/**
 * The extensible fields.
 *
 * <p>An attribute earns a column when code reads it; it stays here when only
 * people read it. So this tab is empty on a fresh install and stays empty
 * until someone defines an attribute in Settings — which is the intended
 * resting state, not a missing feature.</p>
 *
 * <p>Only attributes that apply to the part's group are shown. An empty
 * applicability list means the attribute applies to every group.</p>
 */
export function AttributesTab({
  part,
  values,
  attributes,
  options,
  applicability,
  units,
}: {
  part: PartDTO;
  values: PartAttributeValueDTO[];
  attributes: AttributeDTO[];
  options: AttributeOptionDTO[];
  applicability: Array<{ attribute_id: number; part_group_id: number }>;
  units: UnitDTO[];
}) {
  const { t } = useTranslation();

  const applicable = useMemo(() => {
    const restricted = new Map<number, Set<number>>();
    for (const row of applicability) {
      const set = restricted.get(row.attribute_id) ?? new Set<number>();
      set.add(row.part_group_id);
      restricted.set(row.attribute_id, set);
    }
    return attributes
      .filter((attribute) => attribute.entity === "PART" && attribute.promoted_to_column === null)
      .filter((attribute) => {
        const groups = restricted.get(attribute.id);
        if (!groups) return true;
        return part.part_group_id !== null && groups.has(part.part_group_id);
      })
      .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  }, [attributes, applicability, part.part_group_id]);

  const valueByAttribute = useMemo(
    () => new Map(values.map((value) => [value.attribute_id, value])),
    [values],
  );
  const optionById = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);
  const unitById = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);

  type Row = { id: number; attribute: AttributeDTO; value: PartAttributeValueDTO | undefined };
  const rows: Row[] = applicable.map((attribute) => ({
    id: attribute.id,
    attribute,
    value: valueByAttribute.get(attribute.id),
  }));

  const display = (row: Row): string => {
    const value = row.value;
    if (!value) return "";
    switch (row.attribute.data_type) {
      case "BOOLEAN":
        return value.value_boolean === null
          ? ""
          : value.value_boolean
            ? t("common.yes", "Yes")
            : t("common.no", "No");
      case "NUMBER": {
        const unit = row.attribute.unit_id === null ? "" : ` ${unitById.get(row.attribute.unit_id)?.code ?? ""}`;
        return value.value_number === null ? "" : `${formatNumber(value.value_number)}${unit}`;
      }
      case "DATE":
        return value.value_date ?? "";
      case "ENUM":
        return value.value_option_id === null ? "" : (optionById.get(value.value_option_id)?.label ?? "");
      case "PART_REFERENCE":
        return value.value_part_id === null ? "" : `#${value.value_part_id}`;
      case "DOCUMENT":
        return value.value_object_id === null ? "" : `#${value.value_object_id}`;
      default:
        return value.value_text ?? "";
    }
  };

  const cols = useMemo(() => {
    const c = defineColumns<Row>();
    return [
      c.text("group", (row) => row.attribute.group_name ?? "", {
        headerKey: "column.attribute.group",
        filter: "select",
      }),
      c.text("name", (row) => row.attribute.name, { headerKey: "column.attribute.name", priority: 1 }),
      c.text("value", (row) => display(row), { headerKey: "column.attribute.value", priority: 2 }),
      c.text("data_type", (row) => row.attribute.data_type, {
        headerKey: "column.attribute.type",
        defaultVisible: false,
      }),
    ];
    // `display` closes over the maps rebuilt above; listing them is what keeps
    // the cells in step with a refetch.
  }, [optionById, unitById, valueByAttribute, t]);

  if (attributes.length === 0) {
    return (
      <p className="p-4 text-[13px] text-ink-muted lg:p-6">
        {t(
          "part.attributes.none-defined",
          "No part attributes are defined yet. Define them in Settings → Manufacturing → Part attributes; a field belongs there when people read it and no code does.",
        )}
      </p>
    );
  }

  return (
    <DataTable data={rows} columns={cols} getRowId={(row) => String(row.id)} emptyMessage={nothing(t)} />
  );
}
